import { RelationDto } from "@/modules/common/types";
import { PaymentTypes, PriceRoles, ListingType } from "../dtos";
import { FieldLevel, TrackStatusFormField } from "../dtos/property-stage";
import { InterestAction } from "@/modules/lead-page/types";
import { LeadStatusDto } from "@/modules/property-track-settings/types";

export interface MultiFilterOption {
  key: string;
  ids: number[];
}

export interface TrackSearchFilters {
  customerUId?: string;
  customerName?: string;
  search?: string;
  propertyTypeIds?: number[];
  compoundIds?: number[];
  developerIds?: number[];
  trackTypes?: MultiFilterOption[];
  trackStages?: MultiFilterOption[];
  statuses?: MultiFilterOption[];
  leadStatuses?: MultiFilterOption[];
  unitId?: string;
  websiteListingId?: number;
  numberOfBedroomsFrom?: number;
  numberOfBedroomsTo?: number;
  lastActivityFrom?: Date;
  lastActivityTo?: Date;
  userUIds?: string[];
  unassigned?: boolean;
  deliveryDateFrom?: Date;
  deliveryDateTo?: Date;
  readyToMove?: boolean;
  finishing?: FinishingType[];
  floorNumber?: number;
  unitAreaFrom?: number;
  unitAreaTo?: number;
  downPaymentFrom?: number;
  downPaymentTo?: number;
  priceFrom?: number;
  priceTo?: number;
  askingPriceFrom?: number;
  askingPriceTo?: number;
  priced?: boolean;
  leadTypes?: MultiFilterOption[];
  organizationUIds?: string;
  createdAtFrom?: Date;
  createdAtTo?: Date;
  includeArchived?: boolean;
}

export interface TrackSearchDTO {
  customerUId?: string;
  customerName?: string;
  search?: string;
  propertyTypeIds?: number[];
  compoundIds?: number[];
  developerIds?: number[];
  trackTypeIds?: number[];
  trackStageIds?: number[];
  statusIds?: number[];
  leadStatusIds?: number[];
  unitId?: string;
  websiteListingId?: number;
  numberOfBedroomsFrom?: number;
  numberOfBedroomsTo?: number;
  lastActivityFrom?: Date;
  lastActivityTo?: Date;
  userUIds?: string[];
  unassigned?: boolean;
  deliveryDateFrom?: Date;
  deliveryDateTo?: Date;
  readyToMove?: boolean;
  finishing?: FinishingType[];
  floorNumber?: number;
  unitAreaFrom?: number;
  unitAreaTo?: number;
  downPaymentFrom?: number;
  downPaymentTo?: number;
  priceFrom?: number;
  priceTo?: number;
  askingPriceFrom?: number;
  askingPriceTo?: number;
  priced?: boolean;
  leadTypeIds?: number[];
  organizationUIds?: string;
  createdAtFrom?: Date;
  createdAtTo?: Date;
  journeyUId?: string;
}

export interface PropertyPrice {
  active?: boolean;
  paymentType?: PaymentTypes;
  priceRole: PriceRoles;
  price?: number;
  downPayment?: number;
  maintenance?: number;
  remainingInstallments?: number;
  askingPrice?: number;
  clubhouse?: number;
  parking?: number;
  durationRemainingMonths?: number;
  durationRemainingYears?: number;
  installmentFrequency?: PaymentTerm;
}

export interface PropertyFinishing {
  contractSellingPrice?: number;
  actualSellingPrice?: number;
  estimatedFinishingCost?: number;
  estimatedRentalIncome?: number;
  estimatedRentalPrice?: number;
  forcastedRentalUnits?: number;
  rentalTag?: RentalTag;
}
export interface PropertyRental {
  rentalPrice?: number;
  annualIncreasePercent?: number;
  availableFrom?: Date;
  showingFrom?: Date;
  minimumStayMonths?: number;
  insuranceDepositRequired?: boolean;
  noBrokerCommission?: boolean;
  furnished?: boolean;
  paymentTerms?: PaymentTerm;
}

export interface PropertyHospitality {
  isFeatured?: boolean;
}

export interface Listing {
  uid: string;
  listingType: ListingType;
  active: boolean;
  inventoryListingId: number;
  lastPublishedAt: Date;
  createdAt: Date;
  updatedAt: Date;
  trackDocuments: TrackDocument[];
  propertyPrice: PropertyPrice;
  propertyRental: PropertyRental;
  isRented?: boolean;
  isSold?: boolean;
  status: ListingStatus;
  descriptionAr?: string;
  descriptionEn?: string;
}

/**
 * The available start date implied by a property's running tenancy, read from contract management.
 * When `derived` is true the date is owned by the contract and the form must show it read-only —
 * property management overrides any submitted value anyway.
 */
export interface RentalAvailability {
  derived: boolean;
  availableFrom: Date | null;
  contractEndDate: Date | null;
  offsetDays: number;
  contractUid: string | null;
  annexUid: string | null;
}

export interface RentalAvailabilityDto {
  propertyUid: string;
}

export enum ListingStatus {
  UNPUBLISHED = "Unpublished",
  UNPUBLISHED_EDITS = "Unpublished edits",
  PUBLISHED = "Published",
}

export interface PropertyAmenity {
  amenity: {
    id: number;
    name: string;
  };
}
export interface Customer {
  uid: string;
  name: string;
  telephones?: {
    uid: string;
    telephone: string;
    default: boolean;
  }[];
}

export interface CustomerProperty {
  currentOwner?: boolean;
  customer: Customer;
}

export interface Property {
  uid: string;
  unitId: string;
  description?: string;
  unitArea: number;
  landArea: number;
  numberOfRooms: number;
  numberOfBathrooms: number;
  numberOfBedrooms: number;
  numberOfFloors: number;
  deliveryDate: string;
  readyToMove: boolean;
  phase: string;
  roofArea: number;
  isUnderConstruction: boolean;
  gardenArea: number;
  underMaintenance: boolean;
  estimatedFinishingCost: number;
  estimatedFinishingDate: string;
  estimatedRentalIncome: number;
  websiteListingId: number;
  floorNumber: number;
  finishing: FinishingType;
  lastInstallmentDate?: string;
  latitude?: number;
  longitude?: number;
  address?: string;
  propertyPrice?: PropertyPrice;
  propertyListingPrice?: PropertyPrice;
  propertyFinishing?: PropertyFinishing;
  propertyRental?: PropertyRental;
  propertyAmenity: PropertyAmenity[];
  parentBuildingAmenities?: PropertyAmenity[];
  isSubProperty?: boolean;
  propertyType?: {
    id: number;
    name: string;
    nameAr?: string;
  };
  compound?: Compound;
  customerProperty: CustomerProperty[];
  childPropertiesCount?: number;
  hospitalityType?: HospitalityType;
}

export interface SubProperty extends Property {
  propertyTrack: PropertyTrack[];
}

export interface Compound {
  id: number;
  name: string;
  nameAr?: string;
  developer?: {
    id: number;
    name: string;
  };
  area?: {
    id: number;
    name: string;
  };
}

export interface SubProperty extends Property {
  propertyTrack: PropertyTrack[];
}

export interface Compound {
  id: number;
  uid?: string;
  name: string;
  developer?: {
    id: number;
    name: string;
  };
  area?: {
    id: number;
    name: string;
  };
}

export interface TrackType {
  id: number;
  name: string;
  leadType: RelationDto;
  createCorrespondingLead: boolean;
  trackStagesCount?: number;
}

export interface TrackStage {
  id: number;
  name: string;
  active: boolean;
  precedence: number;
  trackType: TrackType;
  color?: string | null;
}

export interface Status {
  id: number;
  name: string;
  trackStage?: TrackStage;
  color?: string;
}

export interface Journey {
  uid: string;
  status: {
    id: number;
    uid: string;
    name: string;
  };
}

export interface User {
  uid: string;
  name: string;
  email?: string;
  phoneNumber?: string;
  color?: string;
}

export interface Track {
  uid: string;
  scopeOfFinishing?: ScopeOfFinishing;
  updatedAt: Date;
  createdAt: Date;
  archivedAt?: Date;
  journey?: Journey;
  status: Status;
  trackStage?: TrackStage;
  user?: User;
  trackType: TrackType;
  property: Property;
  owner?: {
    uid: string;
    name: string;
  };
}

export interface TrackWithOwner extends Track {
  owner?: {
    uid: string;
    name: string;
    telephone?: string;
  };
}

export interface TrackResponse {
  data: {
    total: number;
    pageSize: number;
    page: number;
    results: Track[];
  };
}

export interface TrackTypeResponse {
  data: {
    total: number;
    pageSize: number;
    page: number;
    results: TrackType[];
  };
}

export interface TrackFiltersProps {
  filtersMemo: TrackSearchFilters;
  setFiltersMemo: (filters: TrackSearchFilters) => void;
}

export interface NextActionDto {
  id: number;
  uid: string;
  description: string;
  completedAt: Date | null;
  createdAt: Date;
  addedBy?: RelationDto;
  reminderFor?: RelationDto;
  status: RelationDto;
}

export const TRACK_DOCUMENT_TYPES = {
  Media: "Media",
  Contracts: "Ownership Contracts",
  PowerOfAttorney: "Power of Attorney",
  OwnerID: "Owner ID",
  MaterialSelection: "Material Selection",
  Proposals: "Proposals",
  Drawings: "Drawings",
  FloorPlans: "Floor Plans",
  MasterPlans: "Master Plans",
  Layouts: "Layouts",
  PrivacyPolicy: "Privacy Policy",
  HouseRules: "House Rules",
  CancellationPolicy: "Cancellation Policy",
  OtherPolicies: "Other Policies",
} as const;

export type TrackDocumentType = keyof typeof TRACK_DOCUMENT_TYPES;

export interface TrackDocument {
  id: number;
  uid: string;
  documentType: TrackDocumentType;
  fileName: string;
  fileUrl: string;
  createdAt: Date;
  updatedAt: Date;
  chosenByClient: boolean;
}

export enum OwnerIDType {
  NationalID = "NationalID",
  Passport = "Passport",
}

export const OWNER_ID_TYPE_LABELS: Record<OwnerIDType, string> = {
  [OwnerIDType.NationalID]: "National ID",
  [OwnerIDType.Passport]: "Passport",
};

export interface OwnerIdImage {
  uid: string;
  fileName: string;
  fileUrl: string;
}

export interface OwnerId {
  uid: string;
  type: OwnerIDType;
  images: OwnerIdImage[];
  createdAt: string | Date;
  updatedAt: string | Date;
}

export interface QueryEstimatedCostDto {
  compoundId?: number;
  propertyTypeId?: number;
  numberOfBedrooms?: number;
  unitArea: number;
  landArea?: number;
}

export interface EstimatedCostDto {
  totalCost: number;
  currency: string;
  range: {
    from: number;
    to: number;
  };
  totalRent: number;
  numberOfYears: number;
}

export interface SupportedPropertyTypeDto {
  id: number;
  name: string;
  numberOfBedrooms: number[];
  hasLand: boolean;
}

export enum FinishingTypes {
  NotFinished = "Not Finished",
  SemiFinished = "Semi Finished",
  Finished = "Finished",
  Furnished = "Furnished",
}

export type FinishingType = keyof typeof FinishingTypes;

export enum RentalTags {
  hospitality = "Hospitality",
  longTerm = "Long Term",
}

export type RentalTag = keyof typeof RentalTags;

export enum HospitalityTypes {
  hotel = "Hotel",
  coastal = "Coastal",
}

export type HospitalityType = keyof typeof HospitalityTypes;

export enum ViewTypes {
  Garden = "Garden",
  City = "City",
  Pool = "Pool",
  Sea = "Sea",
}

export type ViewType = keyof typeof ViewTypes;
export interface TrackDocument {
  documentType: TrackDocumentType;
  file: File;
}

export interface FormSection {
  fieldLevel: FieldLevel;
  fields: TrackStatusFormField[];
}

export interface PropertyTrack {
  uid: string;
  trackStage: TrackStage;
}

export enum PropertyTrackTypes {
  UNLOCKED_FINISHING = "Unlocked finishing",
  UNLOCKED_RENTAL = "Unlocked rental",
  HOSPITALITY = "Unlocked Hospitality",
}

export enum ScopeOfFinishing {
  FinishingAndFurnishing = "FinishingAndFurnishing",
  FinishingOnly = "FinishingOnly",
  FurnishingOnly = "FurnishingOnly",
  PropertyManagement = "PropertyManagement",
}

export const ScopeOfFinishingDisplay: Record<ScopeOfFinishing, string> = {
  [ScopeOfFinishing.FinishingAndFurnishing]: "Finishing + Furnishing + Property Management (Full Unlocked Solution)",
  [ScopeOfFinishing.FinishingOnly]: "Finishing + Property Management",
  [ScopeOfFinishing.FurnishingOnly]: "Furnishing + Property Management",
  [ScopeOfFinishing.PropertyManagement]: "Property Management Only",
} as const;

export enum PaymentTerms {
  monthly = "Monthly",
  quarterly = "Quarterly",
  semiAnually = "Semi anually",
  anually = "Anually",
}

export type PaymentTerm = keyof typeof PaymentTerms;
export interface BaseDropDownConfig {
  placeholder: string;
}

export interface StaticDropDownConfig extends BaseDropDownConfig {
  staticOptions: { label: string; value: any }[];
}

export interface FetchDropDownConfig<T = any> extends BaseDropDownConfig {
  queryKey: string;
  fetchFn: (searchTerm?: string) => Promise<T>;
  transformData: (data: T) => { label: string; value: any }[];
  searchable?: boolean;
}

export type DropDownConfig<T = any> =
  | StaticDropDownConfig
  | FetchDropDownConfig<T>;

export interface QueryInterestsSearchFilters {
  search?: string;
  leadStatusIds?: number[];
  actionTypeIds?: number[];
  userUIds?: string[];
  createdAt?: string;
}

export interface InterestJourney {
  uid: string;
  interestType: RelationDto;
  lastAction?: InterestAction;
  journey: {
    uid: string;
    status: LeadStatusDto;
    customer: Customer;
    user?: User;
  };
  createdAt: Date;
  updatedAt: Date;
  addedBy?: User;
}

export interface InterestJourneyResponse {
  total: number;
  pageSize: number;
  page: number;
  results: InterestJourney[];
}

export interface FinancialProposal {
  uid: string;
  fileName: string;
  fileUrl: string;
  rootVersion: number;
  minorVersion: number;
  createdAt: Date;
  expiresAt?: Date;
}

export interface RoomTypeDto {
  id: number;
  uid: string;
  name: string;
  nameAr: string | null;
}

export interface HospitalityListing {
  id: number;
  uid: string;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  price: number;
  priceForExtraPerson?: number | null;
  currencyCode: string;
  specialInstruction?: string | null;
  checkInTimeStart?: number | null;
  checkInTimeEnd?: number | null;
  checkOutTime?: number | null;
  roomTypeId?: number | null;
  roomType?: RoomTypeDto | null;
  minNights?: number | null;
  maxNights?: number | null;
  airbnbOfficialListingMarkup?: number | null;
  bookingEngineMarkup?: number | null;
  isActive: boolean;
  isFeatured?: boolean;
  maxNumberOfGuests?: number | null;
  bedCount?: number | null;
  singleBedCount: number | null;
  kingBedCount: number | null;
  twinBedCount: number | null;
  queenBedCount: number | null;
  sofaBedCount: number | null;
  viewType?: string | null;
  address?: string | null;
  isExtraBedAllowed?: boolean | null;
  isSmokingAllowed?: boolean | null;
  isPetsAllowed?: boolean | null;
  isPartiesAllowed?: boolean | null;
  instantBookable: boolean;
  hospitalityListingMedia?: {
    id: number;
    uid: string;
    name: string;
    url: string;
    position: number;
    trackDocument: { uid: string };
  }[];
  exceptionalPrices?: {
    uid: string;
    fromDate: string;
    toDate: string;
    price: number;
  }[];
  propertyTrack?: {
    TrackDocument: { uid: string; fileName: string; fileUrl: string; documentType: string }[];
  };
}

export interface QueryListingsDto {
  propertyUid: string;
  listingType: ListingType;
}
export enum DrawingRequestStatus {
  PENDING_ARCHITECTURE = "PENDING_ARCHITECTURE",
  COMPLETED = "COMPLETED",
  APPROVED_FOR_EXECUTION = "APPROVED_FOR_EXECUTION",
}

export enum ProposalType {
  Normal = "Normal",
  NawyNow = "NawyNow",
}

export enum ProjectStatus {
  PENDING_SIGNATURE = "PENDING_SIGNATURE",
}

export interface ProjectSimpleDto {
  uid: string;
  name?: string | null;
  status: ProjectStatus;
  createdAt: string;
}

export interface DrawingRequestDoc {
  uid: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  url: string;
}

export interface DrawingRequestFinishingDoc {
  uid: string;
  fileName: string;
  url: string;
}

export interface DrawingRequest {
  uid: string;
  description: string;
  status: string;
  dueDate: string;
  createdAt: string;
  docs?: DrawingRequestDoc[];
  finishingDocs?: DrawingRequestFinishingDoc[];
  isApprovedForExecution: boolean;
}

export interface CreateDrawingRequestDto {
  description?: string;
  customerUid: string;
  file?: File;
}

export const PROPERTY_TRACK_TAB_OPTIONS = [
  "Activity",
  "Overview",
  "Documents",
  "Construction",
  "SubProperties",
  "LinkedTracks",
  "Listing",
  "Interests",
  "Tenants",
  "DrawingProposals",
  "CustomerIdInfo",
] as const;

export type PropertyTrackTabOptions =
  (typeof PROPERTY_TRACK_TAB_OPTIONS)[number];
